'use client';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import Button from '@mui/material/Button';
import dynamic from 'next/dynamic';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useWindowSize from '../shared/window-size';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@style/variables';
import Image from '@repo/ui/src/components/Image';
import { MediaType } from '@repo/ui/src/types/image';
import { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import { HeaderVariationProps } from '@repo/ui/src/types';

const VideoRendered = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'));

const IMAGE_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/image-link';
const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';
const FULL_TO_THEATER = 1920;

const getUrlInfo = (
  backgroundImage: MediaType,
  width: number,
  xsOnly: boolean,
  imageFullWidthXs: number,
  imageFullWidthSmAndUp: number,
): { isVideo?: boolean; vidWidth?: number; imgUrl?: string } => {
  if (!backgroundImage) {
    return {};
  }
  if (backgroundImage && backgroundImage._meta?.schema === IMAGE_META_SCHEMA) {
    return {
      isVideo: false,
      imgUrl: buildImageSrc(backgroundImage, xsOnly ? imageFullWidthXs : imageFullWidthSmAndUp),
    };
  }

  if (backgroundImage && backgroundImage._meta?.schema === VIDEO_META_SCHEMA) {
    const tmp = width > FULL_TO_THEATER ? FULL_TO_THEATER : width;

    return { isVideo: true, vidWidth: (tmp ?? xsOnly) ? imageFullWidthXs : imageFullWidthSmAndUp };
  }
  return {};
};
interface Props {
  backgroundImage: MediaType;
  title: string;
  subtitle: string;
  youtubeLink?: YoutubeLinkProps;
  description: string;
  buttonText: string;
  showButtonPlayIcon: boolean;
  headerVariation: HeaderVariationProps;
  logo: MediaType;
  logoMaxHeight?: string;
  logoMaxWidth?: string;
  showDivider: boolean;
  _testID: string;
}

const HighlightVideoReference = ({
  backgroundImage,
  title,
  subtitle,
  description,
  youtubeLink,
  buttonText,
  showButtonPlayIcon,
  headerVariation,
  logo,
  logoMaxHeight,
  logoMaxWidth,
  showDivider,
  _testID,
}: Props) => {
  const { push } = useModal(ModalID.video);

  const [width] = useWindowSize();
  const theme = useTheme();
  const smAndDown = useMediaQuery(theme.breakpoints.down('md'));
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'));

  const { imgUrl, isVideo } = getUrlInfo(
    backgroundImage,
    width,
    xsOnly,
    imageFullWidthXs,
    imageFullWidthSmAndUp,
  );

  const logoImgUrl = buildImageSrc(
    logo,
    xsOnly ? imageFullWidthXs - 50 : imageFullWidthSmAndUp,
    0,
    0,
  );

  return (
    <div
      className="tw-relative tw-text-white tw-flex tw-flex-col tw-h-full"
      data-test-id={`highlight_video_reference_${_testID}`}
    >
      {imgUrl && (
        <div className="tw-h-screen tw-max-h-[480px] sm:tw-max-h-[800px] tw-relative">
          <Image src={imgUrl} layout="fill" objectFit="cover" />
        </div>
      )}
      {isVideo && <VideoRendered video={backgroundImage} fillContainer width={1280} />}
      <div className="tw-absolute tw-bottom-0 tw-w-full tw-max-w-full tw-h-full tw-min-h-[480px] tw-text-center tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-[rgba(0,0,0,0.6)]">
        <div className="tw-min-h-[480px] tw-h-full tw-flex tw-justify-center tw-flex-col tw-items-center tw-relative">
          <ToutContent
            lilHeading={title}
            heading={subtitle}
            headingStyleVariant={headerVariation?.headerVariation}
            description={description}
            forceDivider={showDivider}
            dark
          />
          {logo && (
            <LazyLoadImage
              imgComponent={
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={logoImgUrl}
                  alt={logo.name}
                  className="tw-mb-6 tw-max-h-[400px] tw-max-w-full"
                  style={{ maxHeight: logoMaxHeight, maxWidth: logoMaxWidth }}
                />
              }
            />
          )}
          {buttonText && youtubeLink && (
            <div>
              <Button
                tabIndex={0}
                variant="outlined"
                size={smAndDown ? 'small' : 'medium'}
                color="inherit"
                className={`${isVideo && 'tw-mb-4 mb-sm-5'} ${
                  showButtonPlayIcon ? 'tw-px-10' : 'tw-px-8'
                }`}
                onClick={() =>
                  push({ youtubeLink, playlistItems: youtubeLink?.associatedPlaylist?.body })
                }
              >
                {showButtonPlayIcon && <PlayArrowIcon className="tw-mr-2 ml-n2" />}
                {buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HighlightVideoReference;
